import { create } from 'zustand';

interface SnackbarData {
  open: boolean;
  message: string;
  title?: string;
  severity?: 'success' | 'error' | 'info' | 'warning';
}

export interface AppNotification {
  id: string;
  type: string; // 'webex_message', 'system', 'meeting', etc.
  title: string;
  message: string;
  data?: any; // Additional data specific to notification type
  timestamp: string;
  read: boolean;
  userId: string;
}

interface NotificationState {
  // Snackbar notifications (temporary)
  snackbar: SnackbarData;
  notify: (data: Omit<SnackbarData, 'open'>) => void;
  showSnackbar: (message: string, severity?: SnackbarData['severity']) => void;
  closeSnackbar: () => void;

  // Persistent notifications
  notifications: AppNotification[];
  unreadCount: number;
  webexUnreadCount: number;

  // Notification management
  addNotification: (notification: AppNotification) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  markWebexAsRead: () => void;
  clearNotifications: () => void;
  getNotificationsByType: (type: string) => AppNotification[];
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  // Snackbar state
  snackbar: {
    open: false,
    message: '',
  },

  notify: ({ message, title, severity }) => {
    set({
      snackbar: {
        open: true,
        message,
        title,
        severity,
      },
    });
  },

  showSnackbar: (message, event_type = 'info') => {
    set({
      snackbar: {
        open: true,
        message,
        severity: event_type,
      },
    });
  },

  closeSnackbar: () => {
    set({
      snackbar: {
        open: false,
        message: '',
        title: undefined,
        severity: undefined,
      },
    });
  },

  // Persistent notifications state
  notifications: [],
  unreadCount: 0,
  webexUnreadCount: 0,

  addNotification: (notification: AppNotification) => {
    set((state) => {
      const combined = [notification, ...state.notifications];
      const newNotifications = combined.slice(0, 100);
      const dropped = combined.slice(100);

      // Count unread notifications that were trimmed off
      const droppedUnread = dropped.filter((n) => !n.read).length;
      const droppedWebexUnread = dropped.filter(
        (n) => !n.read && n.type === 'webex_message'
      ).length;

      const newUnreadCount = state.unreadCount + 1 - droppedUnread;
      const newWebexUnreadCount =
        (notification.type === 'webex_message'
          ? state.webexUnreadCount + 1
          : state.webexUnreadCount) - droppedWebexUnread;

      return {
        notifications: newNotifications,
        unreadCount: newUnreadCount,
        webexUnreadCount: newWebexUnreadCount,
      };
    });
  },

  markAsRead: (notificationId: string) => {
    set((state) => {
      const notifications = state.notifications.map((notification) => {
        if (notification.id === notificationId && !notification.read) {
          return { ...notification, read: true };
        }
        return notification;
      });

      const unreadCount = notifications.filter((n) => !n.read).length;
      const webexUnreadCount = notifications.filter(
        (n) => !n.read && n.type === 'webex_message'
      ).length;

      return {
        notifications,
        unreadCount,
        webexUnreadCount,
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
      webexUnreadCount: 0,
    }));
  },

  markWebexAsRead: () => {
    set((state) => {
      const notifications = state.notifications.map((notification) => {
        if (notification.type === 'webex_message' && !notification.read) {
          return { ...notification, read: true };
        }
        return notification;
      });

      const unreadCount = notifications.filter((n) => !n.read).length;

      return {
        notifications,
        unreadCount,
        webexUnreadCount: 0,
      };
    });
  },

  clearNotifications: () => {
    set({
      notifications: [],
      unreadCount: 0,
      webexUnreadCount: 0,
    });
  },

  getNotificationsByType: (type: string) => {
    return get().notifications.filter((n) => n.type === type);
  },
}));
